/**
 * Admin Subdomain Redirect
 *
 * Redirects users to /admin-v2 when accessing from admin.subvention360.com
 */

import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAdminDomain } from '@/lib/adminAccess';

/**
 * Hook to handle admin subdomain routing
 * Should be called early in the app to redirect admin subdomain users
 */
export function useAdminSubdomainRedirect() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Only redirect if we're on an admin domain
    if (!isAdminDomain()) return;

    // If we're not already on an admin-v2 path, redirect there
    if (!location.pathname.startsWith('/admin-v2')) {
      // Preserve the path if it's an auth path (for login redirects)
      if (location.pathname.startsWith('/auth')) {
        return;
      }

      navigate('/admin-v2', { replace: true });
    }
  }, [location.pathname, navigate]);
}

/**
 * Component wrapper version of the hook
 */
export const AdminSubdomainRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useAdminSubdomainRedirect();
  return <>{children}</>;
};

export default AdminSubdomainRedirect;
